import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import {
  Select,
  OptionBase,
  ChakraStylesConfig,
  chakraComponents,
  SelectComponentsConfig as ChakraSelectComponentsConfig,
  GroupBase,
  MultiValue,
  SingleValue,
  InputActionMeta,
  OptionsOrGroups,
} from 'chakra-react-select';
import { FC, FocusEventHandler, ReactNode } from 'react';

import { Text, Button, Icon } from 'quotient';

// https://www.w3.org/WAI/ARIA/apg/patterns/combobox/

export type SelectOption<Meta = {}> = OptionBase & {
  value: string;
  label: ReactNode;
} & Meta;

export type SelectComponentsConfig = ChakraSelectComponentsConfig<SelectOption, boolean, GroupBase<SelectOption>>;

export type ComboboxProps = {
  options: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
} & {
  isMulti: boolean;
  value?: MultiValue<SelectOption> | SingleValue<SelectOption>;
  onChange: (newValue: any, actionMeta: any) => void;
  isLoading?: boolean;
  noOptionsMessage?: (obj: { inputValue: string }) => string;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  menuPlacement?: 'auto' | 'top' | 'bottom';
  menuIsOpen?: boolean;
  dataTestId?: string;
  inputId?: string;
  includeDropdownIndicator?: boolean;
  components?: SelectComponentsConfig;
  isClearable?: boolean;
  isDisabled?: boolean;
  isOptionDisabled?: (
    option: SelectOption,
    selectValue: MultiValue<SelectOption> | SingleValue<SelectOption>,
  ) => boolean;
  backgroundColor?: string;
  onMenuScrollToBottom?: () => void;
};
const chakraStyles: ChakraStylesConfig<SelectOption, boolean, GroupBase<SelectOption>> = {
  container: (provided) => ({
    ...provided,
    borderRadius: '4px',
    border: '1px solid',
    borderColor: 'borderNeutralPrimaryMediumHighContrast',
    backgroundColor: 'primaryNeutral.white',
  }),
  control: (provided) => ({
    ...provided,
    borderRadius: '4px',
    border: 'none',
    boxShadow: 'none',
    padding: '7px 12px',
    h: 'auto',
  }),
  input: (provided) => ({
    ...provided,
    textStyle: 'body',
    color: 'gray.800',
    padding: '0',
  }),
  option: (provided, state) => {
    const backgroundColor = () => {
      if (state.isSelected) return 'primary.100';
      if (state.isFocused && !state.isDisabled) return 'secondaryNeutral.200';
      return 'primaryNeutral.white';
    };

    return {
      ...provided,
      backgroundColor: backgroundColor(),
      color: state.isDisabled ? 'primaryNeutral.400' : 'primaryNeutral.900',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      _hover: state.isDisabled
        ? {}
        : {
            backgroundColor: 'secondaryNeutral.200',
          },
      padding: '8px 12px',
      opacity: state.isDisabled ? 0.6 : 1,
    };
  },
  inputContainer: (provided) => ({
    ...provided,
    padding: '0',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontStyle: 'italic',
  }),
  menu: (provided) => ({
    ...provided,
    boxShadow: 'md',
    borderRadius: '4px',
    marginTop: '4px',
    borderColor: 'borderNeutralPrimaryMediumHighContrast',
    zIndex: 10,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '8px 0px',
    borderRadius: '4px',
    borderColor: 'borderNeutralPrimaryMediumHighContrast',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: 'primaryNeutral.500',
    backgroundColor: 'transparent',
    _hover: {
      color: 'primaryNeutral.900',
      backgroundColor: 'transparent',
    },
    width: 'auto',
    height: 'auto',
  }),
  dropdownIndicator: (provided, { selectProps }) => ({
    ...provided,
    background: 'inherit',
    width: '16px',
    padding: '0',
    svg: {
      transform: `rotate(${selectProps.menuIsOpen ? -180 : 0}deg)`,
      transition: 'transform 0.2s',
    },
  }),
  groupHeading: (provided) => ({
    ...provided,
    padding: '8px 12px',
    border: 'none',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
};
const customComponents: SelectComponentsConfig = {
  ClearIndicator: (props: any) => {
    return props.isMulti ? (
      <chakraComponents.ClearIndicator {...props}>
        <Button size="sm" unmask variant="ghost">
          Clear all
        </Button>
      </chakraComponents.ClearIndicator>
    ) : (
      <chakraComponents.ClearIndicator {...props} />
    );
  },
  DropdownIndicator: (props: any) => {
    return (
      <chakraComponents.DropdownIndicator {...props}>
        <Icon color="textHighContrast" icon={faChevronDown} size="xs" />
      </chakraComponents.DropdownIndicator>
    );
  },
  GroupHeading: (props: any) => {
    return (
      <chakraComponents.GroupHeading {...props}>
        <Text color="textMediumContrast" textStyle="capsSubheading">
          {props.children}
        </Text>
      </chakraComponents.GroupHeading>
    );
  },
  Option: (props: any) => {
    return (
      <chakraComponents.Option {...props}>
        <Text textStyle="bodyRegular">{props.children}</Text>
      </chakraComponents.Option>
    );
  },
};

export const QuotientCombobox: FC<ComboboxProps> = ({
  options,
  value,
  onChange,
  onInputChange,
  onBlur,
  isMulti,
  isClearable,
  isDisabled = false,
  isOptionDisabled,
  dataTestId,
  inputId,
  placeholder,
  noOptionsMessage,
  isLoading,
  components = {},
  includeDropdownIndicator = false,
  menuPlacement = 'bottom',
  menuIsOpen,
  backgroundColor,
  onMenuScrollToBottom,
}) => {
  // modifying chakraStyles makes QuotientCombobox impure, so scoping styles
  // here to prevent impurities. If you need to modify a style dynamically, do it like this.
  const styles = { ...chakraStyles };
  if (includeDropdownIndicator) {
    styles.dropdownIndicator = (provided) => {
      return { ...provided, background: 'none' };
    };
  }

  if (backgroundColor) {
    const originalContainer = styles.container;

    styles.container = (provided, state) => ({
      ...(originalContainer ? originalContainer(provided, state) : provided),
      backgroundColor,
    });
  }

  return (
    <Select
      chakraStyles={styles}
      components={{
        ...customComponents,
        ...components,
      }}
      data-testid={dataTestId}
      hasStickyGroupHeaders
      inputId={inputId}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isLoading={isLoading}
      isMulti={Boolean(isMulti)}
      isOptionDisabled={isOptionDisabled}
      menuIsOpen={menuIsOpen}
      menuPlacement={menuPlacement}
      noOptionsMessage={noOptionsMessage}
      options={options}
      placeholder={placeholder}
      value={value}
      onBlur={onBlur}
      onChange={onChange as any}
      onInputChange={onInputChange}
      onMenuScrollToBottom={onMenuScrollToBottom}
    />
  );
};
